/**
 * Resolves when Usercentrics first-layer consent is done, or when UC never
 * appears (local / adblock). One Tap must not prompt while the banner is open:
 * a click on the banner dismisses FedCM and starts Google's cooldown.
 *
 * Second-layer / footer "Cookie settings" after a stored decision must not
 * block or cancel One Tap — only first-layer requirement waits.
 */

export type UsercentricsFirstLayerResult = 'already_decided' | 'decided' | 'timeout'

const UC_FIRST_LAYER_DONE = new Set(['ACCEPT_ALL', 'DENY_ALL', 'SAVE', 'SAVE_CC'])
const UC_FIRST_LAYER_SHOWN = new Set(['CMP_SHOWN'])

export const USERCENTRICS_FIRST_LAYER_TIMEOUT_MS = 10000

type UcUiApi = {
  isConsentRequired?: () => boolean
}

declare global {
  interface Window {
    UC_UI?: UcUiApi
  }
}

const readConsentRequired = (): boolean | null => {
  try {
    if (typeof window.UC_UI?.isConsentRequired !== 'function') {
      return null
    }
    return Boolean(window.UC_UI.isConsentRequired())
  }
  catch {
    // UC_UI may throw before it is fully ready.
    return null
  }
}

export function waitForUsercentricsFirstLayer (
  options: { timeoutMs?: number } = {}
): Promise<UsercentricsFirstLayerResult> {
  const timeoutMs = options.timeoutMs ?? USERCENTRICS_FIRST_LAYER_TIMEOUT_MS

  return new Promise((resolve) => {
    let settled = false
    let waitingForClose = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const cleanup = (): void => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      window.removeEventListener('UC_UI_CMP_EVENT', onCmp as EventListener)
      window.removeEventListener('UC_UI_INITIALIZED', onInitialized)
    }

    const finish = (result: UsercentricsFirstLayerResult): void => {
      if (settled) {
        return
      }
      settled = true
      cleanup()
      resolve(result)
    }

    const armNoUcTimeout = (): void => {
      if (timeoutId !== null || waitingForClose || settled) {
        return
      }
      timeoutId = setTimeout(() => {
        timeoutId = null
        if (settled || waitingForClose) {
          return
        }
        if (readConsentRequired() === true) {
          waitForClose()
          return
        }
        finish('timeout')
      }, timeoutMs)
    }

    const waitForClose = (): void => {
      waitingForClose = true
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const onCmp = (event: Event): void => {
      const type = (event as CustomEvent<{ type?: string }>).detail?.type
      if (!type) {
        return
      }
      if (UC_FIRST_LAYER_SHOWN.has(type)) {
        waitForClose()
        return
      }
      if (UC_FIRST_LAYER_DONE.has(type)) {
        finish('decided')
      }
    }

    const onInitialized = (): void => {
      const required = readConsentRequired()
      if (required === false) {
        finish('already_decided')
        return
      }
      if (required === true) {
        waitForClose()
      }
    }

    window.addEventListener('UC_UI_CMP_EVENT', onCmp as EventListener)
    window.addEventListener('UC_UI_INITIALIZED', onInitialized)

    const requiredAtStart = readConsentRequired()
    if (requiredAtStart === false) {
      finish('already_decided')
      return
    }
    if (requiredAtStart === true) {
      waitForClose()
      return
    }

    armNoUcTimeout()
  })
}

export default waitForUsercentricsFirstLayer
